import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { api } from "../../utils/api";
import GRI2021 from "../../Components/GRIReport/GRI2021";
import GRIHeader from "../../Components/GRIReport/GRIHeader";
import GRI200s from "../../Components/GRIReport/GRI200s";
import GRI300 from "../../Components/GRIReport/GRI300";
import GRI400 from "../../Components/GRIReport/GRI400";

// 👇 these helpers should already exist from your previous implementation
import companyLogo from "../../assets/images/nirantara_logo_new.svg";
import vajra_logo from "../../assets/images/vajra_logo.svg";
import { useUser } from "../../context/AuthContext";

const loadImageAsDataUrl = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else {
        reject(new Error("Could not get canvas context"));
      }
    };
    img.onerror = reject;
    img.src = src;
  });
};

// Helper function to convert SVG to PNG data URL with explicit dimensions
const convertSvgToPng = (svgSrc, width = 200, height = 60) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Draw white background for transparency
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);
        // Draw the SVG image scaled to fit
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      } else {
        reject(new Error("Could not get canvas context"));
      }
    };
    img.onerror = (e) => {
      console.error("Error loading SVG:", e);
      reject(new Error("Failed to load SVG image"));
    };
    img.src = svgSrc;
  });
};

// Helper function to convert all images in HTML to base64
const convertImagesToBase64 = async (htmlContent) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const images = doc.querySelectorAll("img");

  for (const img of Array.from(images)) {
    const src = img.getAttribute("src");
    if (src && !src.startsWith("data:")) {
      try {
        const dataUrl = await loadImageAsDataUrl(src);
        img.setAttribute("src", dataUrl);
      } catch (error) {
        console.warn(`Could not convert image ${src} to base64:`, error);
      }
    }
  }

  return doc.documentElement.outerHTML;
};

const GRIReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportRef = useRef(null);
  const autoDownloadTriggered = useRef(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const { user } = useUser();
  const isAutoDownloadMode = searchParams.get("autoDownload") === "true";

  const downloadReportPdf = async () => {
    try {
      const element = reportRef.current;
      if (!element) {
        console.error("Report container not found");
        return;
      }

      let logoDataUrl;
      try {
        const logoSrc = null; // set your logo url if required
        if (logoSrc) logoDataUrl = await loadImageAsDataUrl(logoSrc);
      } catch (err) {
        console.warn("Logo load failed", err);
      }

      let poweredByLogoDataUrl;
      let vajraImageDataUrl;
      try {
        poweredByLogoDataUrl = await convertSvgToPng(companyLogo, 200, 60);
        vajraImageDataUrl = await convertSvgToPng(vajra_logo, 200, 60);
      } catch (err) {
        console.warn("Powered by logo failed", err);
      }

      await new Promise((r) => setTimeout(r, 1000));

      const canvases = Array.from(element.querySelectorAll("canvas"));
      const canvasDataUrls = canvases.map((canvas) => {
        try {
          return {
            dataUrl: canvas.toDataURL("image/png", 1),
            width: canvas.width,
            height: canvas.height,
            styleWidth: canvas.style.width || `${canvas.width}px`,
            styleHeight: canvas.style.height || `${canvas.height}px`,
          };
        } catch {
          return null;
        }
      });

      const clonedElement = element.cloneNode(true);

      // Remove download button from cloned element
      const downloadButton = clonedElement.querySelector("button");
      if (downloadButton) {
        downloadButton.remove();
      }

      // Remove any elements containing "Generating..." text
      const allElements = clonedElement.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el.textContent && el.textContent.includes("Generating...")) {
          el.remove();
        }
      });

      // Extract and preserve computed styles for MUI Box components (especially GRIHeader)
      const originalBoxes = Array.from(
        element.querySelectorAll(".MuiBox-root"),
      );
      const clonedBoxes = Array.from(
        clonedElement.querySelectorAll(".MuiBox-root"),
      );

      clonedBoxes.forEach((clonedBox, index) => {
        if (originalBoxes[index]) {
          const originalBox = originalBoxes[index];
          const computedStyle = window.getComputedStyle(originalBox);

          // Preserve important styles for GRIHeader green box
          const bgColor = computedStyle.backgroundColor;
          const bgColorRgb = bgColor.replace(/\s/g, ""); // Remove spaces
          if (
            bgColor === "rgb(232, 245, 233)" ||
            bgColorRgb === "rgb(232,245,233)" ||
            bgColor.includes("232, 245, 233") ||
            bgColor.includes("232,245,233")
          ) {
            clonedBox.style.setProperty(
              "background-color",
              "#E8F5E9",
              "important",
            );
            clonedBox.style.setProperty(
              "padding",
              computedStyle.padding || "30px",
              "important",
            );
            clonedBox.style.setProperty(
              "border-radius",
              computedStyle.borderRadius || "8px",
              "important",
            );
            clonedBox.style.setProperty(
              "border",
              computedStyle.border || "1px solid #C8E6C9",
              "important",
            );
            clonedBox.style.setProperty(
              "margin-bottom",
              computedStyle.marginBottom || "40px",
              "important",
            );
            clonedBox.style.setProperty(
              "font-family",
              "Arial, sans-serif",
              "important",
            );
          }

          // Preserve outer container padding
          if (
            computedStyle.padding === "40px" ||
            computedStyle.padding.includes("40px")
          ) {
            clonedBox.style.setProperty("padding", "40px", "important");
            clonedBox.style.setProperty("width", "100%", "important");
            clonedBox.style.setProperty(
              "background-color",
              "#ffffff",
              "important",
            );
            clonedBox.style.setProperty(
              "font-family",
              "Arial, sans-serif",
              "important",
            );
          }
        }
      });

      // Extract and preserve computed styles for Typography components
      const originalTypography = Array.from(
        element.querySelectorAll(".MuiTypography-root"),
      );
      const clonedTypography = Array.from(
        clonedElement.querySelectorAll(".MuiTypography-root"),
      );

      clonedTypography.forEach((clonedTypo, index) => {
        if (originalTypography[index]) {
          const originalTypo = originalTypography[index];
          const computedStyle = window.getComputedStyle(originalTypo);

          // Check if this Typography is inside GRIHeader (check parent for green background)
          const parentBox = originalTypo.closest(".MuiBox-root");
          const isInGRIHeader =
            parentBox &&
            (window
              .getComputedStyle(parentBox)
              .backgroundColor.includes("232, 245, 233") ||
              window
                .getComputedStyle(parentBox)
                .backgroundColor.includes("232,245,233") ||
              window.getComputedStyle(parentBox).backgroundColor ===
                "rgb(232, 245, 233)" ||
              window.getComputedStyle(parentBox).backgroundColor ===
                "rgb(232,245,233)");

          // Preserve title styles for "ORGANIZATIONAL DETAILS"
          if (
            computedStyle.fontSize === "28px" ||
            clonedTypo.textContent.includes("ORGANIZATIONAL DETAILS")
          ) {
            clonedTypo.style.setProperty("font-size", "28px", "important");
            clonedTypo.style.setProperty("font-weight", "bold", "important");
            clonedTypo.style.setProperty(
              "text-transform",
              "uppercase",
              "important",
            );
            clonedTypo.style.setProperty("letter-spacing", "1px", "important");
            clonedTypo.style.setProperty("margin-bottom", "24px", "important");
            clonedTypo.style.setProperty("color", "#000000", "important");
            if (isInGRIHeader) {
              clonedTypo.style.setProperty(
                "font-family",
                "Arial, sans-serif",
                "important",
              );
            }
          }

          // Preserve label styles (16px, font-weight 600)
          if (
            computedStyle.fontSize === "16px" &&
            (computedStyle.fontWeight === "600" ||
              computedStyle.fontWeight === "bold")
          ) {
            clonedTypo.style.setProperty("font-size", "16px", "important");
            clonedTypo.style.setProperty("font-weight", "600", "important");
            clonedTypo.style.setProperty("color", "#000000", "important");
            if (isInGRIHeader) {
              clonedTypo.style.setProperty(
                "font-family",
                "Arial, sans-serif",
                "important",
              );
            }
          }

          // Preserve all Typography inside GRIHeader with Arial font
          if (isInGRIHeader) {
            clonedTypo.style.setProperty(
              "font-family",
              "Arial, sans-serif",
              "important",
            );
            clonedTypo.style.setProperty("color", "#000000", "important");
          }

          // Preserve ReportTable title styles (18px, font-weight 600, margin-bottom 16px)
          // Check if this Typography is a direct child of a Box with margin-bottom 32px (ReportTable container)
          const titleParentBox = originalTypo.parentElement;
          const isReportTableTitle =
            titleParentBox &&
            titleParentBox.classList.contains("MuiBox-root") &&
            (computedStyle.fontSize === "18px" ||
              computedStyle.fontSize === "1.125rem" ||
              (computedStyle.fontWeight === "600" &&
                computedStyle.marginBottom === "16px"));

          if (isReportTableTitle) {
            clonedTypo.style.setProperty("font-size", "18px", "important");
            clonedTypo.style.setProperty("font-weight", "600", "important");
            clonedTypo.style.setProperty("color", "#000000", "important");
            clonedTypo.style.setProperty("margin-bottom", "16px", "important");
            clonedTypo.style.setProperty("margin-top", "0", "important");
            clonedTypo.style.setProperty("display", "block", "important");
            clonedTypo.style.setProperty("line-height", "1.5", "important");
          }
        }
      });

      // Detect and mark serial number columns in tables
      const tables = Array.from(
        clonedElement.querySelectorAll(".MuiTable-root"),
      );
      tables.forEach((table) => {
        const headerRow = table.querySelector(
          ".MuiTableHead-root .MuiTableRow-root",
        );
        if (headerRow) {
          const firstHeaderCell = headerRow.querySelector(
            ".MuiTableCell-head:first-child",
          );
          if (firstHeaderCell) {
            const headerText = firstHeaderCell.textContent.trim().toLowerCase();

            // Check if header is a serial number header
            const isSerialHeader =
              headerText === "s.no." ||
              headerText === "s.no" ||
              headerText === "s.no:" ||
              headerText === "serial no." ||
              headerText === "serial no";

            // Check body cells to confirm if they contain serial numbers
            const bodyRows = table.querySelectorAll(
              ".MuiTableBody-root .MuiTableRow-root",
            );
            let hasSerialBodyContent = false;

            if (isSerialHeader) {
              // If header suggests serial, check body cells
              bodyRows.forEach((row) => {
                const firstBodyCell = row.querySelector(
                  ".MuiTableCell-body:first-child",
                );
                if (firstBodyCell) {
                  const bodyText = firstBodyCell.textContent.trim();
                  // Check if body cell contains a serial number pattern (e.g., "1.", "2.", "10.")
                  if (/^\d+\.?\s*$/.test(bodyText)) {
                    hasSerialBodyContent = true;
                  }
                }
              });
            }

            // Only mark as serial if header is serial AND body contains serial numbers
            const isSerialNumber =
              isSerialHeader && (bodyRows.length === 0 || hasSerialBodyContent);

            if (isSerialNumber) {
              // Mark serial number cells with a data attribute
              firstHeaderCell.setAttribute("data-is-serial", "true");
              bodyRows.forEach((row) => {
                const firstBodyCell = row.querySelector(
                  ".MuiTableCell-body:first-child",
                );
                if (firstBodyCell) {
                  firstBodyCell.setAttribute("data-is-serial", "true");
                }
              });
            } else {
              // Remove narrow width constraints for non-serial first columns
              firstHeaderCell.setAttribute("data-is-serial", "false");
              firstHeaderCell.style.setProperty("width", "auto", "important");
              firstHeaderCell.style.setProperty(
                "min-width",
                "auto",
                "important",
              );
              firstHeaderCell.style.setProperty(
                "max-width",
                "none",
                "important",
              );
              firstHeaderCell.style.setProperty(
                "padding",
                "12px 16px",
                "important",
              );
              firstHeaderCell.style.setProperty(
                "text-align",
                "left",
                "important",
              );

              bodyRows.forEach((row) => {
                const firstBodyCell = row.querySelector(
                  ".MuiTableCell-body:first-child",
                );
                if (firstBodyCell) {
                  firstBodyCell.setAttribute("data-is-serial", "false");
                  firstBodyCell.style.setProperty("width", "auto", "important");
                  firstBodyCell.style.setProperty(
                    "min-width",
                    "auto",
                    "important",
                  );
                  firstBodyCell.style.setProperty(
                    "max-width",
                    "none",
                    "important",
                  );
                  firstBodyCell.style.setProperty(
                    "padding",
                    "12px 16px",
                    "important",
                  );
                  firstBodyCell.style.setProperty(
                    "text-align",
                    "left",
                    "important",
                  );
                }
              });
            }
          }
        }
      });

      const clonedCanvases = Array.from(
        clonedElement.querySelectorAll("canvas"),
      );

      clonedCanvases.forEach((canvas, i) => {
        const imgData = canvasDataUrls[i];
        if (imgData) {
          const img = document.createElement("img");
          img.src = imgData.dataUrl;
          img.style.width = imgData.styleWidth;
          img.style.height = imgData.styleHeight;
          img.style.maxWidth = "100%";
          canvas.replaceWith(img);
        }
      });

      const cssStyles = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      
      * { box-sizing: border-box; }
      
      body {
        font-family: Inter, Arial, sans-serif;
        margin: 0;
        color: #000;
        line-height: 1.6rem !important;
      }
      
      /* ================= GRI HEADER CUSTOM CSS ================= */
      .gri-header-container {
        width: 100% !important;
        padding: 10px 40px 40px 40px !important;
        background-color: #ffffff !important;
        font-family: Arial, sans-serif !important;
        box-sizing: border-box !important;
        margin: 0 !important;
      }
      
      .gri-header-green-box {
        background-color:#C4F67980 !important;
        background:#C4F67980 !important;
        padding: 30px !important;
        border-radius: 8px !important;
        margin-bottom: 40px !important;
        border: 1px #C4F67980 #C8E6C9 !important;
        font-family: Arial, sans-serif !important;
        width: 100% !important;
        box-sizing: border-box !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      
      .gri-header-title {
        font-size: 28px !important;
        font-weight: bold !important;
        color: #000000 !important;
        text-transform: uppercase !important;
        margin-bottom: 24px !important;
        letter-spacing: 1px !important;
        font-family: Arial, sans-serif !important;
        margin-top: 0 !important;
      }
      
      .gri-header-content {
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
        gap: 12px !important;
      }
      
      .gri-header-row {
        display: grid !important;
        grid-template-columns: 360px 1fr !important;
        column-gap: 12px !important;
        align-items: start !important;
        font-size: 16px !important;
        line-height: 1.6 !important;
        color: #000000 !important;
        font-family: Arial, sans-serif !important;
        width: 100% !important;
      }
      
      .gri-header-value {
        font-weight: 400 !important;
        font-size: 16px !important;
        color: #000000 !important;
        font-family: Arial, sans-serif !important;
      }
      
      .gri-header-label {
        font-weight: 700 !important;
        font-size: 16px !important;
        color: #000000 !important;
        font-family: Arial, sans-serif !important;
      }

      /* ================= COMMON HEADING / TITLE STYLE ================= */
      .common_text_style {
        font-size: 16px !important;
        font-weight: 600 !important;
        color: #000000 !important;
      }

      
      /* Preserve all inline styles from MUI components (for other components) */
      [style] {
        /* Inline styles are preserved automatically */
      }
      
      .MuiBox-root {
        display: block !important;
        width: 100% !important;
      }
      
      /* Regular Typography (not in GRIHeader) - use Inter */
      .MuiTypography-root {
        font-family: Inter, Arial, sans-serif !important;
        color: #000 !important;
      }
      
      /* Typography spans for labels and values (outside GRIHeader) */
      .MuiTypography-root span {
        font-size: 16px !important;
        color: #000000 !important;
      }
      
      .MuiTypography-h4,
      .MuiTypography-h5,
      .MuiTypography-h6 {
        font-weight: 600 !important;
        margin-bottom: 12px !important;
      }
      
      .MuiTypography-body1,
      .MuiTypography-body2 {
        font-size: 14px !important;
      }
      
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      
      /* ================= GENERIC TABLE ================= */
      table {
        width: 100%;
        border-collapse: collapse;
      }
      
      th, td {
        border: 1px solid #ddd;
        padding: 8px;
        font-size: 14px;
      }
      
      thead th {
        background: #1C486B;
        color: #fff;
        font-weight: 600;
        text-align: left;
      }
      
      tbody tr {
        background: #fff;
      }
      
      /* ================= CSS MODULE TABLE SUPPORT ================= */
      table[class*="summary_table"],
      table[class*="emission_breakdown_table"] {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #ddd;
      }
      
      table[class*="summary_table"] thead,
      table[class*="emission_breakdown_table"] thead {
        background-color: #1C486B;
      }
      
      table[class*="summary_table"] th,
      table[class*="emission_breakdown_table"] th {
        background-color: #1C486B;
        color: #fff;
        font-weight: 600;
      }
      
      table[class*="summary_table"] td,
      table[class*="emission_breakdown_table"] td {
        background-color: #fff;
      }
      
      /* ================= MUI TABLE FIX (ReportTable.jsx) ================= */
      .MuiTable-root {
        width: 100% !important;
        border-collapse: collapse !important;
        border: 1px solid #828282 !important;
      }
      
      .MuiTableHead-root {
        background-color: #1C486B !important;
      }
      
      .MuiTableCell-head {
        background-color: #1C486B !important;
        color: #fff !important;
        font-weight: 700 !important;
        font-size: 16px !important;
        padding: 12px 16px !important;
        border: 1px solid #828282 !important;
      }
      
      /* Fix S.No. column width in header - only for serial number columns */
      .MuiTableHead-root .MuiTableCell-head:first-child[data-is-serial="true"] {
        width: 60px !important;
        min-width: 60px !important;
        max-width: 60px !important;
        padding: 12px 8px !important;
        text-align: center !important;
        white-space: nowrap !important;
        overflow: hidden !important;
      }
      
      /* Normal width for non-serial first columns in header */
      .MuiTableHead-root .MuiTableCell-head:first-child:not([data-is-serial="true"]) {
        width: auto !important;
        min-width: auto !important;
        max-width: none !important;
        padding: 12px 16px !important;
        text-align: left !important;
      }
      
      .MuiTableCell-body {
        background-color: #F2F6F8 !important;
        color: #000000 !important;
        font-size: 16px !important;
        padding: 12px 16px !important;
        border: 1px solid #828282 !important;
      }
      
      /* Ensure cells with inline backgroundColor style are respected */
      .MuiTableCell-body[style*="background-color"] {
        background-color: #F2F6F8 !important;
      }
      
      /* Fix S.No. column width in body - only for serial number columns */
      .MuiTableBody-root .MuiTableCell-body:first-child[data-is-serial="true"] {
        width: 60px !important;
        min-width: 60px !important;
        max-width: 60px !important;
        padding: 12px 8px !important;
        text-align: center !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        background-color: #F2F6F8 !important;
      }
      
      /* Normal width for non-serial first columns in body */
      .MuiTableBody-root .MuiTableCell-body:first-child:not([data-is-serial="true"]) {
        width: auto !important;
        min-width: auto !important;
        max-width: none !important;
        padding: 12px 16px !important;
        text-align: left !important;
        background-color: #F2F6F8 !important;
      }
      
      .MuiTableRow-root {
        page-break-inside: avoid !important;
      }
      
      .MuiPaper-root {
        box-shadow: none !important;
        border-radius: 4px !important;
        border: 1px solid #828282 !important;
        background: #fff !important;
      }
      
      /* ================= REPORT TABLE TITLE CSS ================= */
      /* ReportTable title styling - Typography before table */
      .MuiBox-root .MuiTypography-root {
        font-family: Inter, Arial, sans-serif !important;
        color: #000000 !important;
      }
      
      /* Specific styling for ReportTable titles */
      .MuiBox-root > .MuiTypography-root:first-child {
        font-size: 18px !important;
        font-weight: 600 !important;
        color: #000000 !important;
        margin-bottom: 16px !important;
        margin-top: 0 !important;
        display: block !important;
        line-height: 1.5 !important;
      }
      
      /* Alternative selector for ReportTable titles */
      .MuiBox-root[style*="margin-bottom: 32px"] > .MuiTypography-root {
        font-size: 18px !important;
        font-weight: 600 !important;
        color: #000000 !important;
        margin-bottom: 16px !important;
        margin-top: 0 !important;
        display: block !important;
        line-height: 1.5 !important;
      }
      
      /* ================= PAGE BREAK ================= */
      .page_break,
      .pageBreak {
        display: block;
        height: 0;
        page-break-before: always !important;
        break-before: page !important;
      }
      
      /* ================= GRI REPORT MODULE CSS (report.module.css) ================= */
      .container,
      [class*="report_container"] {
        width: 100% !important;
        padding: 40px !important;
        background-color: #ffffff !important;
        font-family: Arial, sans-serif !important;
        box-sizing: border-box !important;
      }

          .mainHeading,
      [class*="mainHeading"] {
        font-family: Inter;
        font-weight: bold;
        font-size: 24px;
        line-height: 1.2;
        letter-spacing: 1px;
        text-transform: uppercase;
        margin-top: 8px !important;
        margin-bottom: 6px !important;
      }

      .container,
      [class*="report_container"] {
        padding: 16px 40px !important;
      }

      .mainHeading + .mainHeading,
      [class*="mainHeading"] + [class*="mainHeading"] {
        margin-top: 4px !important;
      }



      
      .subSectionTitle {
        font-size: 18px;
        font-weight: 600;
        color: #000000;
        margin-bottom: 24px;
      }
      
      .sectionHeading,
      [class*="sectionHeading"] {
        font-family: Inter;
        font-weight: bold;
        font-size: 1.125rem;
        line-height: 140%;
        letter-spacing: 1px;
        color: #eb7e0e;
        text-transform: uppercase;
        margin-bottom: 19px;
        margin-top: 30px;
        display: flex;
        align-items: center;
        position: relative;
        padding-left: 12px;
        border-left: 3px solid #eb7e0e;
        width: fit-content;
        text-align: left;
        height: 1.4rem;
      }
      
      .productName,
      [class*="productName"] {
        font-size: 1rem !important;
        font-weight: bold !important;
        color: #000000 !important;
        margin-bottom: 0.5rem !important;
        margin-top: 1.5rem !important;
        display: block !important;
      }
      
      .productInfo,
      [class*="productInfo"] {
        font-size: 1rem !important;
        color: #000000 !important;
        margin-bottom: 1rem !important;
        display: block !important;
      }
      
      .totalsContainer {
        display: flex !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 2rem !important;
        margin-top: 1.5rem !important;
        margin-bottom: 1.5rem !important;
        margin-left: 1.2rem !important;
      }
      
      .totalLabel {
        font-weight: bold !important;
        margin-bottom: 0.5rem !important;
      }
      
      .totalValue {
        margin-bottom: 1rem !important;
      }
      
      .subSectionMargin {
        margin-top: 2rem !important;
      }
      
      .subQuestionTitle {
        font-size: 0.9375rem !important;
        font-weight: normal !important;
        margin-bottom: 0.5rem !important;
        margin-top: 0 !important;
        display: block !important;
      }
      
      .consolidatingSection {
        margin-top: 40px !important;
      }
      
      .consolidatingTitle {
        font-size: 16px !important;
        font-weight: 600 !important;
        color: #000000 !important;
        margin-bottom: 24px !important;
        margin-top: 0 !important;
        display: block !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
        max-width: 100% !important;
        line-height: 1.5 !important;
        white-space: normal !important;
        text-indent: -1.2em !important;
        padding-left: 1.5em !important;
        box-sizing: border-box !important;
      }
      
      .questionsContainer {
        margin-left: 16px !important;
      }
      
      .sameLineGroup,
      [class*="sameLineGroup"] {
        display: flex !important;
        gap: 40px !important;
        align-items: baseline !important;
        flex-wrap: nowrap !important;
        white-space: nowrap !important;
        overflow: visible !important;
      }
      
      .questionBox {
        margin-bottom: 24px !important;
        display: inline-block !important;
      }
 
     

      
      .questionTitle {
        font-size: 14px !important;
        font-weight: 500 !important;
        color: #000000 !important;
        margin-bottom: 12px !important;
        margin-top: 0 !important;
        display: block !important;
      }
      
      .questionAnswer {
        font-size: 12px !important;
        color: #000000 !important;
        margin-left: 16px !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        line-height: 1.6 !important;
        display: block !important;
        font-weight: 400 !important;
      }
      
      .questionRow {
        display: flex !important;
        align-items: baseline !important;
        margin-bottom: 12px !important;
        white-space: nowrap !important;
        flex-wrap: nowrap !important;
        overflow: hidden !important;
      }
      
      .questionRowInline {
        display: inline-flex !important;
        align-items: baseline !important;
        white-space: nowrap !important;
      }
      
      .questionLabel {
        font-size: 16px !important;
        font-weight: 600 !important;
        white-space: nowrap !important;
        margin-right: 8px !important;
        display: inline !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
      
      .questionValue {
        font-size: 16px !important;
        white-space: nowrap !important;
        display: inline-block !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
      
      .loadingContainer {
        padding: 40px !important;
        text-align: center !important;
      }
      
      .tableContainer {
        margin-bottom: 32px !important;
        border: 1px solid #000000 !important;
        border-radius: 4px !important;
        overflow: hidden !important;
      }
      
      .table {
        width: 100% !important;
        border-collapse: collapse !important;
        background-color: #ffffff !important;
      }
      
      .tableHeader {
        background-color: #1C486B !important;
      }
      
      .tableHeaderCell {
        color: #ffffff !important;
        font-weight: bold !important;
        font-size: 16px !important;
        padding: 12px 16px !important;
        border: 1px solid #000000 !important;
        border-bottom: 2px solid #000000 !important;
        text-align: left !important;
      }
      
      .tableRow {
        background-color: #ffffff !important;
      }
      
      .tableRowEven {
        background-color: #f5f5f5 !important;
      }
      
      .tableCell {
        padding: 12px 16px !important;
        font-size: 16px !important;
        color: #000000 !important;
        border: 1px solid #000000 !important;
        text-align: left !important;
        background-color: #F2F6F8 !important;
      }
      
      .tableCellCenter {
        text-align: center !important;
      }
      
      .inputContainer {
        display: flex !important;
        align-items: center !important;
        gap: 0.5rem !important;
        margin-top: 1.5rem !important;
        margin-bottom: 1rem !important;
        margin-left: 1rem !important;
      }
      
      .inputContainerLarge {
        display: flex !important;
        align-items: center !important;
        gap: 2rem !important;
        margin-top: 1.5rem !important;
        margin-bottom: 1rem !important;
        margin-left: 1rem !important;
      }
      
      .inputField {
        padding: 0.5rem 0.75rem !important;
        font-size: 1rem !important;
        border: 1px solid #ccc !important;
        border-radius: 4px !important;
        width: 200px !important;
        font-family: Arial, sans-serif !important;
        box-sizing: border-box !important;
      }
      
      .inputField:disabled {
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
      }
      
      .inputField:focus {
        outline: none !important;
        border-color: #1C486B !important;
      }
      
      .textareaField {
        padding: 0.5rem 0.75rem !important;
        font-size: 1rem !important;
        border: 1px solid #ccc !important;
        border-radius: 4px !important;
        width: 98% !important;
        margin-left: 1rem !important;
        font-family: Arial, sans-serif !important;
        box-sizing: border-box !important;
        resize: vertical !important;
        min-height: 2.5rem !important;
      }
      
      .textareaField:disabled {
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
      }
      
      .textareaField:focus {
        outline: none !important;
        border-color: #1C486B !important;
      }
      
      .selectField {
        padding: 0.5rem 0.75rem !important;
        font-size: 1rem !important;
        border: 1px solid #ccc !important;
        border-radius: 4px !important;
        height: 2rem !important;
        width: 24% !important;
        font-family: Arial, sans-serif !important;
        box-sizing: border-box !important;
        background-color: #ffffff !important;
      }
      
      .selectField:disabled {
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
      }
      
      .selectField:focus {
        outline: none !important;
        border-color: #1C486B !important;
      }
      
      .formSection {
        margin-bottom: 1.5rem !important;
      }

.noDataCell {
  padding: 12px 16px !important;
  font-size: 16px !important;
  text-align: center !important;
  color: #666666 !important;
  border: 1px solid #828282 !important;
  font-style: italic !important;
  background-color: #F2F6F8 !important;
}

      `;

      let htmlContent = clonedElement.innerHTML;
      htmlContent = await convertImagesToBase64(`<div>${htmlContent}</div>`);
      htmlContent = new DOMParser().parseFromString(htmlContent, "text/html")
        .body.innerHTML;

      const html = `
        <html>
          <head>
            <meta charset="UTF-8" />
            <style>
              ${cssStyles}
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `;

      const apiBaseUrl =
        sessionStorage.getItem("apiBaseUrl") ||
        api ||
        "https://uat-api.nirantara.ai";
      const baseUrl = apiBaseUrl.replace(/\/api\/v1$/, "");

      const res = await fetch(`${baseUrl}/gri-generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          html,
          logoDataUrl,
          poweredByLogoDataUrl,
          skipFirstPageHeader: false,
          companyId: user?.companyId,
          userId: user?.id,
          companyImage: user?.company?.companyImage,
          vajraImageDataUrl,
        }),
      });

      if (!res.ok) throw new Error("PDF generation failed");

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "GRI_Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("PDF download error:", error);
      return false;
    }
  };

  const handleDownloadGri = async () => {
    setDownloadingId(id);
    try {
      return await downloadReportPdf();
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!isAutoDownloadMode || autoDownloadTriggered.current) return;

    autoDownloadTriggered.current = true;
    const returnTo = searchParams.get("returnTo") || "/gri";

    const runAutoDownload = async () => {
      await handleDownloadGri();
      navigate(returnTo);
    };

    runAutoDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoDownloadMode, searchParams, navigate]);

  return (
    <>
      {downloadingId === id && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "white",
              p: 4,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#1C486B" }}>
              Generating your report...
            </Typography>
          </Box>
        </div>
      )}

      <div
        ref={reportRef}
        style={
          isAutoDownloadMode
            ? {
                position: "fixed",
                left: "-99999px",
                top: "-99999px",
                width: "1200px",
                overflow: "hidden",
                pointerEvents: "none",
              }
            : {
                padding: "20px",
                maxHeight: "100vh",
                overflowY: "auto",
                position: "relative",
              }
        }
      >
        {!isAutoDownloadMode && (
          <Box sx={{ position: "absolute", top: 20, right: 20 }}>
            <Button
              variant="contained"
              onClick={handleDownloadGri}
              disabled={downloadingId === id}
              sx={{ bgcolor: "#1C486B" }}
            >
              {downloadingId === id ? "Generating..." : "Download Report"}
            </Button>
          </Box>
        )}

        <GRIHeader />
        {/* <div className="page_break" /> */}
        <GRI2021 />
        <div className="page_break" />
        <GRI200s />
        <div className="page_break" />
        <GRI300 />
        <div className="page_break" />
        <GRI400 />
      </div>
    </>
  );
};

export default GRIReport;
